import { useRef, useState } from 'react'
import { Image as ImageIcon, Loader2, Star, Trash2, Plus, AlertTriangle } from 'lucide-react'
import { patchJson } from '../../lib/api.js'
import { generateThumbnail } from '../../lib/thumbnail.js'
import { uploadFile } from '../../lib/uploadDirect.js'
import { assetImages, imageThumbSrc } from '../../lib/asset.js'
import ImageLightbox from './ImageLightbox.jsx'
import ConfirmDialog from '../ConfirmDialog.jsx'

const MAX = 12

function blobToDataURL(blob) {
  return new Promise((resolve) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result)
    r.onerror = () => resolve('')
    r.readAsDataURL(blob)
  })
}

export default function AssetImages({ asset, onChanged }) {
  const images = assetImages(asset)
  const [uploading, setUploading] = useState(false)
  const [busyKey, setBusyKey] = useState('')
  const [err, setErr] = useState('')
  const [lb, setLb] = useState(null)
  const [confirmImg, setConfirmImg] = useState(null)
  const fileRef = useRef(null)

  async function onPick(e) {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (!files.length) return
    setUploading(true); setErr('')
    try {
      const added = []
      for (const file of files) {
        if (images.length + added.length >= MAX) break
        if (!/^image\//.test(file.type || '')) continue
        // Direct to Wasabi where CORS allows, else the old proxy — uploadDirect.js.
        const obj = await uploadFile('/api/asset-upload', { asset_id: String(asset.id), kind: 'img' }, file)
        const thumb = await generateThumbnail(file).catch(() => null)
        if (thumb) obj.thumbnail = await blobToDataURL(thumb)
        obj.is_profile = false
        added.push(obj)
      }
      // If the asset has no profile yet, the first newly-added becomes profile.
      const existing = Array.isArray(asset.image) ? asset.image : []
      const hasProfile = existing.some((f) => f?.is_profile)
      if (!hasProfile && added.length) added[0].is_profile = true
      await patchJson(`/api/assets/${asset.id}`, { image: [...existing, ...added] })
      onChanged?.()
    } catch (e) {
      setErr(String(e.message || e))
    } finally {
      setUploading(false)
    }
  }

  async function setProfile(img) {
    setBusyKey(img.location); setErr('')
    try {
      const next = (Array.isArray(asset.image) ? asset.image : []).map((f) => ({ ...f, is_profile: f.location === img.location }))
      await patchJson(`/api/assets/${asset.id}`, { image: next })
      onChanged?.()
    } catch (e) { setErr(String(e.message || e)) } finally { setBusyKey('') }
  }

  async function removeImage(img) {
    setConfirmImg(null)
    setBusyKey(img.location); setErr('')
    try {
      if (img.location) fetch(`/api/asset-file/${img.location.split('/').map(encodeURIComponent).join('/')}`, { method: 'DELETE', credentials: 'include' }).catch(() => {})
      let next = (Array.isArray(asset.image) ? asset.image : []).filter((f) => f.location !== img.location)
      // If we removed the profile, promote the first remaining image.
      if (img.is_profile && next.length && !next.some((f) => f.is_profile)) next = next.map((f, i) => ({ ...f, is_profile: i === 0 }))
      await patchJson(`/api/assets/${asset.id}`, { image: next })
      onChanged?.()
    } catch (e) { setErr(String(e.message || e)) } finally { setBusyKey('') }
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center"><ImageIcon size={16} strokeWidth={2} /></span>
          <h3 className="text-[14.5px] font-semibold text-slate-800">รูปภาพสินทรัพย์ <span className="text-slate-400 font-normal">({images.length})</span></h3>
        </div>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading || images.length >= MAX}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100 disabled:opacity-50"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} เพิ่มรูป
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPick} className="hidden" />
      </div>

      {err && <div className="flex items-center gap-1.5 text-[12.5px] text-rose-600 mb-2"><AlertTriangle size={13} /> {err}</div>}

      {!images.length ? (
        <div className="text-center py-6 text-[13px] text-slate-400">ยังไม่มีรูปภาพ — กด “เพิ่มรูป” เพื่ออัปโหลด (รูปแรกจะเป็นรูปโปรไฟล์)</div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
          {images.map((img, i) => (
            <div key={img.location} className={`relative group rounded-xl overflow-hidden ring-1 ${img.is_profile ? 'ring-2 ring-brand-400' : 'ring-slate-200'}`}>
              <button type="button" onClick={() => setLb(i)} className="block w-full aspect-square">
                <img src={imageThumbSrc(img)} alt={img.name || ''} className="w-full h-full object-cover" />
              </button>
              {img.is_profile && (
                <span className="absolute top-1 left-1 inline-flex items-center gap-0.5 rounded-full bg-brand-600 text-white text-[9.5px] font-semibold px-1.5 py-0.5">
                  <Star size={9} className="fill-white" /> โปรไฟล์
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-end gap-1 p-1 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition">
                {!img.is_profile && (
                  <button onClick={() => setProfile(img)} disabled={busyKey === img.location} title="ตั้งเป็นรูปโปรไฟล์" className="p-1 rounded-md bg-white/90 text-slate-600 hover:text-brand-600">
                    {busyKey === img.location ? <Loader2 size={12} className="animate-spin" /> : <Star size={12} />}
                  </button>
                )}
                <button onClick={() => setConfirmImg(img)} disabled={busyKey === img.location} title="ลบรูป" className="p-1 rounded-md bg-white/90 text-slate-600 hover:text-rose-600">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ImageLightbox images={images} index={lb} onClose={() => setLb(null)} onIndex={setLb} />

      {confirmImg && (
        <ConfirmDialog
          title="ลบรูปนี้?"
          message="รูปจะถูกลบออกจากคลังภาพของสินทรัพย์ถาวร"
          confirmLabel="ลบรูป" tone="danger"
          onConfirm={() => removeImage(confirmImg)}
          onCancel={() => setConfirmImg(null)}
        />
      )}
    </section>
  )
}
